/**
 * DataTable (DS foundation). A real <table> (screen readers get rows/columns/headers for free) with: sticky header,
 * sortable columns (aria-sort), numeric/currency columns right-aligned in tabular figures, two densities with fixed
 * row heights, truncation with a tooltip that only appears when text is cut, pagination, row selection with a
 * bulk-action bar, row click to open a detail, optional column visibility, and empty / loading (skeleton) / error
 * states. Below a 40rem container width each row becomes a labelled card (container query, not viewport).
 * No grid library: rows are rendered as given; page sizes stay small (server pagination).
 */
import { useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { Button, Skeleton, Tooltip } from "./components.js";
import { ErrorState } from "./feedback.js";
import { Menu } from "./selection.js";

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ");

export type SortDir = "asc" | "desc";
export type SortState = { key: string; dir: SortDir } | null;

export type Column<R> = {
  key: string;
  header: string;
  render: (row: R) => ReactNode;
  /** Numbers and money: right-aligned, tabular figures, mono. */
  numeric?: boolean;
  align?: "start" | "center" | "end";
  /** Track width ("8rem", "20%"); the identifying column usually takes the rest. */
  width?: string;
  /** Header is a sort button; the page applies the sort (usually server side) from onSortChange. */
  sortable?: boolean;
  /** First direction when this column becomes the sort (default: desc for numeric, asc otherwise). */
  sortFirst?: SortDir;
  /**
   * Cut long text to one line with an ellipsis; the full text shows in a tooltip, only when it is cut.
   * "custom": the cell gets the truncating layout but `render` places its own <Truncate> (text + a badge).
   */
  truncate?: boolean | "custom";
  /** Plain text of the cell for the truncation tooltip and the stacked-card label (defaults to the rendered text). */
  text?: (row: R) => string;
  /** The row's identifying column (name, BOL): card title on narrow widths, never hideable. */
  primary?: boolean;
  /** Can be turned off from the Columns menu (default true unless primary). */
  hideable?: boolean;
  /** Hidden until turned on from the Columns menu. */
  defaultHidden?: boolean;
  /** Narrow (card) layout: "hide" drops low-value columns from the card. */
  mobile?: "show" | "hide";
  /**
   * A quiet second line under the cell (requester under a customer, email under a name, summary under a title).
   * One line, cut with an ellipsis; the full text shows in a tooltip only when cut. Return null for none.
   * A node (a link to the customer) needs `subText` for the tooltip.
   */
  sub?: (row: R) => ReactNode;
  subText?: (row: R) => string;
};

export type DataTableProps<R> = {
  /** Accessible name of the table (visually hidden). */
  caption: string;
  columns: ReadonlyArray<Column<R>>;
  rows: readonly R[];
  rowKey: (r: R) => string;
  density?: "comfortable" | "compact";
  /** Empty state: text, or a node (EmptyState). */
  empty?: ReactNode;
  sort?: SortState;
  onSortChange?: (sort: SortState) => void;
  /** First load: skeleton rows. With rows already shown: kept, marked busy (no flash). */
  loading?: boolean;
  /** Load failure: replaces the body with an error state and a retry. */
  error?: { title?: string; message?: ReactNode; onRetry?: () => void } | null;
  /** Row click opens the detail. Clicks on links, buttons and checkboxes inside the row are left alone. */
  onRowClick?: (row: R) => void;
  /** The detail URL: Cmd/Ctrl/middle click on a row opens it in a new tab. Put a real link in the primary cell too. */
  rowHref?: (row: R) => string;
  /** Row selection with a bulk-action bar. */
  selection?: {
    selected: ReadonlySet<string>;
    onChange: (next: Set<string>) => void;
    /** Actions for the selected rows (rendered in the bar). */
    actions?: (keys: string[], clear: () => void) => ReactNode;
    /** Name read for a row's checkbox ("Select Acme Warehouse"). */
    rowLabel?: (row: R) => string;
  };
  /** Pagination footer. `total` shows "1–50 of 230"; without it, `hasMore` drives Next. */
  pagination?: { page: number; pageSize: number; total?: number; hasMore?: boolean; onPageChange: (page: number) => void };
  /** Show the Columns menu (column visibility). Controlled with `hidden` + `onHiddenChange`, else kept internally. */
  columnsMenu?: boolean;
  hidden?: ReadonlySet<string>;
  onHiddenChange?: (next: Set<string>) => void;
  /** Max height of the scroll area; the header sticks inside it. */
  maxHeight?: string;
  /** Rows marked for attention (left stripe). */
  rowTone?: (row: R) => "danger" | "warning" | null;
  className?: string;
};

const INTERACTIVE = "a,button,input,select,textarea,label,summary,[role='checkbox'],[role='menuitem'],[role='combobox'],[data-no-row-click]";

function SortIcon({ dir }: { dir: SortDir | null }) {
  return (
    <svg className="ffx-dt__sorticon" viewBox="0 0 16 16" aria-hidden="true" data-dir={dir ?? undefined}>
      <path className="ffx-dt__up" d="M5 6.5 8 3.5l3 3" />
      <path className="ffx-dt__down" d="M5 9.5 8 12.5l3-3" />
    </svg>
  );
}
function ColumnsIcon() {
  return (
    <svg className="ffc-ico" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="2.5" y="3" width="11" height="10" rx="1.5" />
      <path d="M6.25 3v10M9.75 3v10" />
    </svg>
  );
}

/** Next sort when a header is pressed: a new column starts at its first direction, the same column flips. */
export function nextSort(current: SortState, key: string, first: SortDir): SortState {
  if (!current || current.key !== key) return { key, dir: first };
  return { key, dir: current.dir === "asc" ? "desc" : "asc" };
}

/** A sortable primitive. Null/undefined/"" sort last in both directions. */
export type SortPrimitive = string | number | null | undefined;
/** Value a column sorts by (client-side sorting): a getter per row. */
export type SortValue<R = unknown> = (row: R) => SortPrimitive;
/** Either one `(row, key)` function or a record of per-column getters. */
export type SortSource<R> = ((row: R, key: string) => SortPrimitive) | Readonly<Record<string, SortValue<R>>>;

/**
 * Sort rows the API returned whole (queues, reconciliation, AmFast, articles, staff) by a column. `values` is a
 * `(row, key)` function or a record of per-column getters: numbers compare numerically, strings with the locale
 * collator (numeric aware: "FF-9" < "FF-10", case-insensitive). Missing values sort last in both directions.
 * Stable: ties keep the API's order.
 */
export function sortRows<R>(rows: readonly R[], sort: SortState, values: SortSource<R>): R[] {
  const get = !sort ? undefined : typeof values === "function" ? (r: R) => values(r, sort.key) : values[sort.key];
  if (!sort || !get) return [...rows];
  const coll = new Intl.Collator("en-US", { numeric: true, sensitivity: "base" });
  const sign = sort.dir === "asc" ? 1 : -1;
  return rows
    .map((r, i) => ({ r, i, v: get(r) }))
    .sort((a, b) => {
      const an = a.v === null || a.v === undefined || a.v === "";
      const bn = b.v === null || b.v === undefined || b.v === "";
      if (an || bn) return an === bn ? a.i - b.i : an ? 1 : -1;
      const c = typeof a.v === "number" && typeof b.v === "number" ? a.v - b.v : coll.compare(String(a.v), String(b.v));
      return c === 0 ? a.i - b.i : c * sign;
    })
    .map((x) => x.r);
}

/** One page of rows the API returned whole (client pagination for DataTable's `pagination` with `total`). */
export function pageRows<R>(rows: readonly R[], page: number, pageSize: number): R[] {
  return rows.slice(page * pageSize, page * pageSize + pageSize);
}

/** "1–50 of 230" / "51–73" for the pager. Exported for tests. */
export function pageRange(page: number, pageSize: number, shown: number, total?: number): string {
  if (shown === 0) return total ? `0 of ${total.toLocaleString("en-US")}` : "No results";
  const from = page * pageSize + 1;
  const to = page * pageSize + shown;
  return total !== undefined ? `${from.toLocaleString("en-US")}–${to.toLocaleString("en-US")} of ${total.toLocaleString("en-US")}` : `${from.toLocaleString("en-US")}–${to.toLocaleString("en-US")}`;
}

/** One line, ellipsis, full text in a tooltip only when the text is actually cut (keyboard focus shows it too). */
export function Truncate({ text, children, maxWidth }: { text: string; children?: ReactNode; maxWidth?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const cut = () => !!ref.current && ref.current.scrollWidth > ref.current.clientWidth + 1;
  return (
    <Tooltip content={text} when={cut}>
      <span ref={ref} className="ffx-trunc" style={maxWidth ? { maxInlineSize: maxWidth } : undefined}>
        {children ?? text}
      </span>
    </Tooltip>
  );
}

/**
 * Sorting and pagination in the browser, for lists the API returns whole (articles, campaigns, staff, a report's
 * rows). Spread the result into DataTable (`<DataTable {...client} …/>`): rows, sort, onSortChange, and pagination
 * only when there is more than one page. A new sort goes back to the first page.
 */
export function useClientRows<R>(rows: readonly R[], { values, initial = null, pageSize = 50 }: { values: Readonly<Record<string, SortValue<R>>>; initial?: SortState; pageSize?: number }) {
  const [sort, setSort] = useState<SortState>(initial);
  const [page, setPage] = useState(0);
  const sorted = useMemo(() => sortRows(rows, sort, values), [rows, sort, values]);
  const last = Math.max(0, Math.ceil(sorted.length / pageSize) - 1);
  const p = Math.min(page, last);
  return {
    rows: sorted.slice(p * pageSize, (p + 1) * pageSize),
    sort,
    onSortChange: (s: SortState) => {
      setSort(s);
      setPage(0);
    },
    pagination: sorted.length > pageSize ? { page: p, pageSize, total: sorted.length, onPageChange: setPage } : undefined,
  };
}

const SKELETON_W = ["72%", "48%", "60%", "40%", "66%", "54%"];

export function DataTable<R>({
  caption,
  columns,
  rows,
  rowKey,
  density = "comfortable",
  empty,
  sort = null,
  onSortChange,
  loading = false,
  error = null,
  onRowClick,
  rowHref,
  selection,
  pagination,
  columnsMenu = false,
  hidden: hiddenProp,
  onHiddenChange,
  maxHeight,
  rowTone,
  className,
}: DataTableProps<R>) {
  const [hiddenInner, setHiddenInner] = useState<Set<string>>(() => new Set(columns.filter((c) => c.defaultHidden).map((c) => c.key)));
  const hidden = hiddenProp ?? hiddenInner;
  const setHidden = (next: Set<string>) => (onHiddenChange ? onHiddenChange(next) : setHiddenInner(next));
  const visible = useMemo(() => columns.filter((c) => !hidden.has(c.key) || c.primary), [columns, hidden]);
  const keys = useMemo(() => rows.map(rowKey), [rows, rowKey]);
  const sel = selection?.selected;
  const selectedOnPage = sel ? keys.filter((k) => sel.has(k)) : [];
  const allOn = !!sel && keys.length > 0 && selectedOnPage.length === keys.length;
  const someOn = !!sel && selectedOnPage.length > 0 && !allOn;
  const span = visible.length + (selection ? 1 : 0) + (columnsMenu ? 1 : 0);
  const firstLoad = loading && rows.length === 0 && !error;
  const clickable = !!onRowClick;

  const toggleRow = (k: string) => {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    selection.onChange(next);
  };
  const toggleAll = () => {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (allOn) keys.forEach((k) => next.delete(k));
    else keys.forEach((k) => next.add(k));
    selection.onChange(next);
  };
  const clear = () => selection?.onChange(new Set());
  const onRow = (e: MouseEvent<HTMLTableRowElement>, r: R) => {
    if (!onRowClick) return;
    if ((e.target as Element).closest(INTERACTIVE)) return;
    if (window.getSelection?.()?.toString()) return; // selecting text to copy is not a click
    if ((e.metaKey || e.ctrlKey || e.button === 1) && rowHref) {
      window.open(rowHref(r), "_blank", "noopener");
      return;
    }
    onRowClick(r);
  };
  const align = (c: Column<R>) => (c.numeric ? "end" : (c.align ?? "start"));
  const cellClass = (c: Column<R>) => cx(align(c) === "center" && "ffx-dt__c", c.truncate && "ffx-dt__cell--trunc", c.mobile === "hide" && "ffx-dt__cell--mhide", c.primary && "ffx-dt__cell--primary", align(c) === "end" && "ffx-table__num", c.numeric && "ffx-num");

  const bulk = selection && sel && sel.size > 0;
  return (
    <div className={cx("ffx-dt", density === "compact" && "ffx-dt--compact", clickable && "ffx-dt--clickable", className)}>
      {bulk ? (
        <div className="ffx-dt__bulk" role="region" aria-label="Bulk actions">
          <span className="ffx-dt__bulkcount ffx-num">{sel.size} selected</span>
          <div className="ffx-dt__bulkactions">{selection.actions?.([...sel], clear)}</div>
          <button type="button" className="ffc-link" onClick={clear}>
            Clear selection
          </button>
        </div>
      ) : null}
      <div className={cx("ffx-table-wrap ffx-dt__scroll", density === "compact" && "ffx-table--compact")} style={maxHeight ? { maxBlockSize: maxHeight } : undefined} aria-busy={loading || undefined}>
        <table className="ffx-table ffx-dt__table">
          <caption className="ffx-sr">{caption}</caption>
          <colgroup>
            {selection ? <col className="ffx-dt__colsel" /> : null}
            {visible.map((c) => (
              <col key={c.key} style={c.width ? { inlineSize: c.width, width: c.width } : undefined} />
            ))}
            {columnsMenu ? <col className="ffx-dt__colmenu" /> : null}
          </colgroup>
          <thead>
            <tr>
              {selection ? (
                <th scope="col" className="ffx-dt__sel">
                  <input
                    type="checkbox"
                    className="ffx-dt__check"
                    aria-label="Select all rows on this page"
                    checked={allOn}
                    ref={(el) => {
                      if (el) el.indeterminate = someOn;
                    }}
                    onChange={toggleAll}
                    disabled={rows.length === 0}
                  />
                </th>
              ) : null}
              {visible.map((c) => {
                const on = sort?.key === c.key ? sort.dir : null;
                return (
                  <th
                    key={c.key}
                    scope="col"
                    aria-sort={c.sortable ? (on === "asc" ? "ascending" : on === "desc" ? "descending" : "none") : undefined}
                    className={cx(align(c) === "end" && "ffx-table__num", align(c) === "center" && "ffx-dt__c", c.mobile === "hide" && "ffx-dt__cell--mhide")}
                  >
                    {c.sortable && onSortChange ? (
                      <button type="button" className={cx("ffx-dt__sort", on && "is-on")} onClick={() => onSortChange(nextSort(sort, c.key, c.sortFirst ?? (c.numeric ? "desc" : "asc")))}>
                        <span>{c.header}</span>
                        <SortIcon dir={on} />
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
              {columnsMenu ? (
                <th scope="col" className="ffx-dt__menu">
                  <Menu
                    look="icon"
                    size="sm"
                    trigger={<ColumnsIcon />}
                    triggerLabel="Choose columns"
                    items={[
                      { type: "label", label: "Columns" },
                      ...columns
                        .filter((c) => !c.primary && c.hideable !== false)
                        .map((c) => ({
                          type: "checkbox" as const,
                          key: c.key,
                          label: c.header,
                          checked: !hidden.has(c.key),
                          onCheckedChange: (on: boolean) => {
                            const next = new Set(hidden);
                            if (on) next.delete(c.key);
                            else next.add(c.key);
                            setHidden(next);
                          },
                        })),
                    ]}
                  />
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {error ? (
              <tr className="ffx-dt__staterow">
                <td colSpan={span}>
                  <ErrorState title={error.title ?? `Couldn't load ${caption.toLowerCase()}`} message={error.message} onRetry={error.onRetry} compact />
                </td>
              </tr>
            ) : firstLoad ? (
              Array.from({ length: Math.min(pagination?.pageSize ?? 6, 8) }, (_, i) => (
                <tr key={`sk${i}`} className="ffx-dt__skel" aria-hidden="true">
                  {selection ? <td className="ffx-dt__sel"><Skeleton width="1rem" height="1rem" radius="0.25rem" /></td> : null}
                  {visible.map((c, j) => (
                    <td key={c.key} className={cellClass(c)}>
                      <Skeleton width={c.numeric ? "3.5rem" : SKELETON_W[(i + j) % SKELETON_W.length]} />
                    </td>
                  ))}
                  {columnsMenu ? <td /> : null}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr className="ffx-dt__staterow">
                <td colSpan={span} className="ffx-table__empty">
                  {empty ?? "Nothing here yet."}
                </td>
              </tr>
            ) : (
              rows.map((r, i) => {
                const k = keys[i]!;
                const on = !!sel?.has(k);
                const tone = rowTone?.(r);
                return (
                  <tr key={k} aria-selected={selection ? on : undefined} data-tone={tone ?? undefined} className={cx(on && "is-selected")} onClick={clickable ? (e) => onRow(e, r) : undefined} onAuxClick={clickable && rowHref ? (e) => onRow(e, r) : undefined}>
                    {selection ? (
                      <td className="ffx-dt__sel">
                        <input type="checkbox" className="ffx-dt__check" aria-label={`Select ${selection.rowLabel?.(r) ?? k}`} checked={on} onChange={() => toggleRow(k)} />
                      </td>
                    ) : null}
                    {visible.map((c) => {
                      const content = c.render(r);
                      const main = c.truncate === true ? <Truncate text={c.text?.(r) ?? (typeof content === "string" ? content : "")}>{content}</Truncate> : content;
                      const sub = c.sub?.(r);
                      return (
                        <td key={c.key} data-label={c.header} className={cx(cellClass(c), sub ? "ffx-dt__cell--sub" : null)}>
                          {sub ? (
                            <span className="ffx-dt__stack">
                              {main}
                              <Truncate text={c.subText?.(r) ?? (typeof sub === "string" ? sub : "")}>
                                <span className="ffx-dt__sub">{sub}</span>
                              </Truncate>
                            </span>
                          ) : (
                            main
                          )}
                        </td>
                      );
                    })}
                    {columnsMenu ? <td className="ffx-dt__menu" /> : null}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {loading && rows.length > 0 ? <span className="ffx-dt__busy" aria-hidden="true" /> : null}
      </div>
      {pagination ? (
        <nav className="ffx-dt__pager" aria-label={`${caption} pages`}>
          <span className="ffx-dt__range ffx-num" aria-live="polite">
            {firstLoad ? "Loading…" : pageRange(pagination.page, pagination.pageSize, rows.length, pagination.total)}
          </span>
          <div className="ffx-dt__pagebtns">
            <Button size="sm" variant="ghost" disabled={pagination.page === 0} onClick={() => pagination.onPageChange(pagination.page - 1)}>
              Previous
            </Button>
            <span className="ffx-dt__page ffx-num">Page {pagination.page + 1}</span>
            <Button
              size="sm"
              variant="ghost"
              disabled={pagination.total !== undefined ? (pagination.page + 1) * pagination.pageSize >= pagination.total : !pagination.hasMore}
              onClick={() => pagination.onPageChange(pagination.page + 1)}
            >
              Next
            </Button>
          </div>
        </nav>
      ) : null}
    </div>
  );
}
